import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { AddButton } from 'app/components/Button/AddButton';
import { CustomSpinner } from 'app/components/Spinner';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useAuth } from 'app/modules/auth';

import { getResponseError } from 'app/helpers/errors';
import { usePageGoBack } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';
import { stringify } from 'qs';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconRedirect } from 'app/assets/icons/icon-redirect.svg';
import { ReactComponent as IconSyncWhite } from 'app/assets/icons/icon-sync-white.svg';

import { StatusStockUp } from './core/enum';
import { getReplenishmentCheck, getStockRuns, sendBpoRequest, sendGiRequests } from './core/requests';
import { GiRequestStatus, ReplenishmentCheckItem, StockRun } from './core/type';
import EmptyCard from './components/EmptySelectItemCard';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const ELIGIBLE_STATUSES = [StatusStockUp.Posted, StatusStockUp.WIP, StatusStockUp['Past-due']];

const Th = styled.th`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  color: var(--neutral-6);
  white-space: nowrap;
  padding: 16px 12px;
  text-align: left;
  border-bottom: 1px solid var(--neutral-3);
`;

const Td = styled.td`
  font-size: 14px;
  line-height: 26px;
  color: var(--neutral-10);
  padding: 16px 12px;
  vertical-align: middle;
`;

const Row = styled.tr`
  &:nth-child(even) {
    background: var(--neutral-1);
  }
`;

type StatusTone = 'shortfall' | 'giPending' | 'availableForGi';

const STATUS_TONE_STYLE: Record<StatusTone, { bg: string; color: string; label: string }> = {
  shortfall: { bg: 'var(--warning-light-3)', color: 'var(--warning-active-2)', label: 'PR Required' },
  giPending: { bg: 'var(--primary-3)', color: 'var(--primary)', label: 'Pick List Pending' },
  availableForGi: { bg: 'var(--success-light-2)', color: 'var(--success)', label: 'Ready for Pick List' },
};

const StatusPill = styled.span<{ tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  background: ${(props) => STATUS_TONE_STYLE[props.tone].bg};
  color: ${(props) => STATUS_TONE_STYLE[props.tone].color};
`;

const getStatusTone = (item: ReplenishmentCheckItem): StatusTone => {
  if (item.shortfallQty > 0) return 'shortfall';
  if (item.giStatus === GiRequestStatus.Pending) return 'giPending';
  return 'availableForGi';
};

const ReplenishmentCheckPage = () => {
  const { currentUser } = useAuth();
  const policy = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = policy.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);
  const goBack = usePageGoBack(PATH.INVENTORY_STOCK_RUN);

  const isLoading = useBoolean(false);
  const isSendingGi = useBoolean(false);
  const [stockRuns, setStockRuns] = useState<StockRun[]>([]);
  const [items, setItems] = useState<ReplenishmentCheckItem[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [bpoRequestingIds, setBpoRequestingIds] = useState<Set<string>>(new Set());

  const stockRunNameById = useMemo(() => {
    const map = new Map<string, string>();
    stockRuns.forEach((run) => map.set(String(run.id), run.index));
    return map;
  }, [stockRuns]);

  const stockRunIds = useMemo(
    () =>
      stockRuns
        .map((run) => run.id)
        .filter((id): id is number | string => id !== null && id !== undefined),
    [stockRuns],
  );

  const fetchReplenishmentCheck = async () => {
    try {
      isLoading.setValue(true);
      const orgParam = isSystemAdminCurrent ? `&orgId=${organization?.id}` : '';
      const statusParam = ELIGIBLE_STATUSES.map((status) => `&status[]=${status}`).join('');

      const stockRunsRes = await getStockRuns(
        stringify({ limit: 9999999, page: 1, orderBy: 'createdAt', orderDirection: 'DESC' }) +
          statusParam +
          orgParam,
      );
      const runs = stockRunsRes.data?.items ?? [];
      setStockRuns(runs);

      if (runs.length === 0) {
        setItems([]);
        return;
      }

      const runIds = runs
        .map((run) => run.id)
        .filter((id): id is number | string => id !== null && id !== undefined);
      const replenishmentItems = await getReplenishmentCheck(runIds);
      setItems(replenishmentItems);
    } catch (error) {
      getResponseError(error);
      setItems([]);
    } finally {
      setHasFetched(true);
      isLoading.setValue(false);
    }
  };

  const handleSendGi = async () => {
    if (stockRunIds.length === 0) {
      return;
    }

    try {
      isSendingGi.setValue(true);
      const updatedItems = await sendGiRequests(stockRunIds);
      setItems(updatedItems);
      toast.success('Pick List requested for all eligible items.');
    } catch (error) {
      getResponseError(error);
    } finally {
      isSendingGi.setValue(false);
    }
  };

  const handleBpoRequest = async (item: ReplenishmentCheckItem) => {
    const itemKey = String(item.id);

    setBpoRequestingIds((prev) => new Set(prev).add(itemKey));
    try {
      await sendBpoRequest(item.id, item.stockRunIds);
      toast.success(`BPO request sent for ${item.name}.`);
    } catch (error) {
      getResponseError(error);
    } finally {
      setBpoRequestingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  useEffect(() => {
    fetchReplenishmentCheck();
  }, []);

  return (
    <RootContentPage
      title=""
      header={
        <div className="d-flex align-items-center w-100 justify-content-between">
          <div className="d-flex align-items-center">
            <IconRedirect
              style={{
                cursor: 'pointer',
              }}
              onClick={goBack}
            />
            <div
              className="h-100 d-flex align-items-center"
              style={{
                borderRight: `1px solid var(--neutral-4)`,
                paddingRight: 24,
                marginLeft: 16,
              }}
            >
              <Typography variant="heading-6">Replenishment Check</Typography>
            </div>
          </div>

          <div className="d-flex align-items-center">
            <AddButton
              title="Send Pick List"
              color="white"
              icon={<></>}
              style={{ margin: '0 12px' }}
              loading={isSendingGi.value}
              disabled={isLoading.value || stockRunIds.length === 0}
              onClick={handleSendGi}
            />
            <AddButton
              title="Check Replenishment"
              color="primary"
              loading={isLoading.value}
              icon={<IconSyncWhite style={{ marginRight: 10 }} />}
              onClick={fetchReplenishmentCheck}
            />
          </div>
        </div>
      }
    >
      {isLoading.value && <LoadingCard />}

      {!isLoading.value && hasFetched && items.length === 0 && (
        <EmptyCard label="No outstanding replenishment needed for active stock runs" />
      )}

      {!isLoading.value && items.length > 0 && (
        <div className="table-responsive">
          <table className="table align-middle table-row-dashed dataTable no-footer">
            <thead>
              <tr>
                <Th>Item</Th>
                <Th>SKU</Th>
                <Th>Unit</Th>
                <Th>Stock Runs</Th>
                <Th>Outstanding Qty</Th>
                <Th>Available Qty (SmartWMS)</Th>
                <Th>Std Price</Th>
                <Th>Shortfall</Th>
                <Th>Status</Th>
                {/*<Th />*/}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const tone = getStatusTone(item);
                const stockRunNames = item.stockRunIds
                  .map((id) => stockRunNameById.get(String(id)))
                  .filter(Boolean)
                  .join(', ');
                const isRequestingBpo = bpoRequestingIds.has(String(item.id));

                return (
                  <Row key={item.id}>
                    <Td>{item.name}</Td>
                    <Td>{item.sku}</Td>
                    <Td>{item.unit}</Td>
                    <Td>{stockRunNames}</Td>
                    <Td>{item.outstandingQty}</Td>
                    <Td>{item.smartwmsAvailableQty}</Td>
                    <Td>{(item.stdPrice ?? 0).toFixed(2)}</Td>
                    <Td>
                      <Typography color={tone === 'shortfall' ? 'danger' : 'neutral-10'} fontWeight={600}>
                        {item.shortfallQty}
                      </Typography>
                    </Td>
                    <Td>
                      <StatusPill tone={tone}>{STATUS_TONE_STYLE[tone].label}</StatusPill>
                    </Td>
                    <Td>
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        disabled={isRequestingBpo}
                        onClick={() => handleBpoRequest(item)}
                      >
                        {isRequestingBpo ? <CustomSpinner /> : 'BPO Request'}
                      </button>
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </RootContentPage>
  );
};

export default ReplenishmentCheckPage;
